import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash, FloppyDisk, X, Barbell, DownloadSimple } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useWeightUnit } from "@/lib/units";
import ExerciseDemoButton from "@/components/ExerciseDemoButton";

export default function WorkoutLogger({ workouts, templates, api, onRefresh, onOpenWorkout, onNewPRs, quickLogTemplate, onQuickLogConsumed }) {
  const { unit: weightUnit, fromKg, toKg, label: weightLabel } = useWeightUnit();
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [duration, setDuration] = useState(45);
  const [exercises, setExercises] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Sort templates so user-created (non-preset) appear first
  const sortedTemplates = [...(templates || [])].sort((a, b) => {
    const aPreset = a.is_preset ? 1 : 0;
    const bPreset = b.is_preset ? 1 : 0;
    if (aPreset !== bPreset) return aPreset - bPreset;
    return (a.name || "").localeCompare(b.name || "");
  });

  const addExercise = () => {
    setExercises([...exercises, { name: "", sets: [{ reps: 0, weight: 0, completed: false }], notes: "" }]);
  };

  const removeExercise = (idx) => {
    setExercises(exercises.filter((_, i) => i !== idx));
  };

  const updateExercise = (idx, field, value) => {
    const updated = [...exercises];
    updated[idx] = { ...updated[idx], [field]: value };
    setExercises(updated);
  };

  const addSet = (exIdx) => {
    const updated = [...exercises];
    updated[exIdx].sets.push({ reps: 0, weight: 0, completed: false });
    setExercises(updated);
  };

  const removeSet = (exIdx, setIdx) => {
    const updated = [...exercises];
    updated[exIdx].sets = updated[exIdx].sets.filter((_, i) => i !== setIdx);
    setExercises(updated);
  };

  const updateSet = (exIdx, setIdx, field, value) => {
    const updated = [...exercises];
    updated[exIdx].sets[setIdx] = { ...updated[exIdx].sets[setIdx], [field]: value };
    setExercises(updated);
  };

  const loadTemplate = (templateId) => {
    const t = templates.find((tpl) => tpl.id === templateId);
    if (t) {
      setName(t.name);
      setExercises(t.exercises.map((e) => ({
        ...e,
        sets: e.sets.map((s) => ({ ...s, completed: false })),
      })));
      toast.success(`Loaded template: ${t.name}`);
    }
  };

  // Quick Log: open dialog pre-filled when a template is passed from the Workouts page
  useEffect(() => {
    if (quickLogTemplate) {
      setName(quickLogTemplate.name || "");
      setDate(new Date().toISOString().split("T")[0]);
      setDuration(quickLogTemplate.duration_minutes || 45);
      setExercises(
        (quickLogTemplate.exercises || []).map((e) => ({
          ...e,
          sets: (e.sets && e.sets.length > 0)
            ? e.sets.map((s) => ({ ...s, completed: false }))
            : [{ reps: 0, weight: 0, completed: false }],
        }))
      );
      setShowForm(true);
      onQuickLogConsumed?.();
    }
  }, [quickLogTemplate, onQuickLogConsumed]);

  const saveWorkout = async () => {
    if (!name.trim()) { toast.error("Workout name required"); return; }
    if (exercises.length === 0) { toast.error("Add at least one exercise"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${api}/workouts`, { name, date, duration_minutes: duration, exercises, notes });
      toast.success("Workout logged!");
      setShowForm(false);
      resetForm();
      onRefresh();
      // Check for new PRs
      if (res.data?.new_prs?.length > 0) {
        onNewPRs?.(res.data.new_prs);
      }
    } catch (e) {
      toast.error("Failed to save workout");
    } finally {
      setSaving(false);
    }
  };

  const deleteWorkout = async (id) => {
    try {
      await axios.delete(`${api}/workouts/${id}`);
      toast.success("Workout deleted");
      onRefresh();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const resetForm = () => {
    setName("");
    setDate(new Date().toISOString().split("T")[0]);
    setDuration(45);
    setExercises([]);
    setNotes("");
  };

  return (
    <div className="space-y-6" data-testid="workout-logger-page">
      <div className="flex items-center justify-between">
        <div>
          <h2
            className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Workout Log
          </h2>
          <p className="text-sm text-[#71717A] mt-1">Track every rep, every set</p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const isEmpty = !workouts || workouts.length === 0;
              if (isEmpty) {
                toast.error("Log a workout first to export");
                return;
              }
              window.open(`${api}/workouts/export.csv`, "_blank", "noopener");
            }}
            className="bg-[#0F0F0F] border border-[#27272A] hover:border-[#E05A1D] text-white font-bold uppercase tracking-wide px-4 py-2.5 text-sm flex items-center gap-2 transition-all active:scale-[0.98]"
            data-testid="export-csv-btn"
            title="Export all workouts as CSV"
          >
            <DownloadSimple size={16} weight="bold" />
            <span className="hidden sm:inline">Export CSV</span>
          </button>
          <Dialog open={showForm} onOpenChange={setShowForm}>
            <DialogTrigger asChild>
              <button
                className="bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide px-5 py-2.5 text-sm flex items-center gap-2 transition-colors active:scale-[0.98]"
                data-testid="new-workout-btn"
              >
                <Plus size={16} weight="bold" /> New Workout
              </button>
            </DialogTrigger>
          <DialogContent className="bg-[#161616] border-[#27272A] max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle
                className="text-xl font-bold uppercase tracking-tight text-white"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Log Workout
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-2">
              {/* Template loader */}
              {sortedTemplates.length > 0 && (
                <div>
                  <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">
                    Load From Template
                  </label>
                  <Select onValueChange={loadTemplate}>
                    <SelectTrigger className="bg-[#0F0F0F] border-[#27272A] text-white" data-testid="template-select">
                      <SelectValue placeholder="Select template..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C1C1C] border-[#27272A]">
                      {sortedTemplates.some((t) => !t.is_preset) && (
                        <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-[#E05A1D]">Your Workouts</div>
                      )}
                      {sortedTemplates.filter((t) => !t.is_preset).map((t) => (
                        <SelectItem key={t.id} value={t.id} className="text-white hover:bg-[#262626]" data-testid={`template-option-${t.id}`}>
                          {t.name}
                        </SelectItem>
                      ))}
                      {sortedTemplates.some((t) => !t.is_preset) && sortedTemplates.some((t) => t.is_preset) && (
                        <div className="px-2 py-1 mt-1 text-[10px] font-bold uppercase tracking-[0.1em] text-[#71717A] border-t border-[#27272A]">Workouts</div>
                      )}
                      {sortedTemplates.filter((t) => t.is_preset).map((t) => (
                        <SelectItem key={t.id} value={t.id} className="text-white hover:bg-[#262626]" data-testid={`template-option-${t.id}`}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] focus:ring-1 focus:ring-[#E05A1D] outline-none transition-all"
                    placeholder="e.g. Push Day"
                    data-testid="workout-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Date</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] focus:ring-1 focus:ring-[#E05A1D] outline-none transition-all"
                    data-testid="workout-date-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Duration (min)</label>
                <input
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value) || 0)}
                  className="w-32 bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] focus:ring-1 focus:ring-[#E05A1D] outline-none transition-all"
                  data-testid="workout-duration-input"
                />
              </div>

              {/* Exercises */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A]">Exercises</label>
                  <button
                    onClick={addExercise}
                    className="text-xs text-[#E05A1D] hover:text-[#F06B2E] font-bold uppercase flex items-center gap-1 transition-colors"
                    data-testid="add-exercise-btn"
                  >
                    <Plus size={14} weight="bold" /> Add Exercise
                  </button>
                </div>

                <div className="space-y-3">
                  {exercises.map((ex, exIdx) => (
                    <div key={exIdx} className="border border-[#27272A] p-3 bg-[#0F0F0F]" data-testid={`exercise-${exIdx}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="text"
                          value={ex.name}
                          onChange={(e) => updateExercise(exIdx, "name", e.target.value)}
                          className="flex-1 bg-transparent border-b border-[#27272A] text-white text-sm py-1 focus:border-[#E05A1D] outline-none"
                          placeholder="Exercise name"
                          data-testid={`exercise-name-${exIdx}`}
                        />
                        {ex.name && (
                          <ExerciseDemoButton api={api} name={ex.name} label="" size={16} />
                        )}
                        <button
                          onClick={() => removeExercise(exIdx)}
                          className="text-[#71717A] hover:text-red-500 transition-colors"
                          data-testid={`remove-exercise-${exIdx}`}
                        >
                          <Trash size={16} />
                        </button>
                      </div>

                      {/* Sets */}
                      <div className="space-y-1.5">
                        <div className="grid grid-cols-[1fr_2fr_2fr_auto] gap-2 text-xs text-[#71717A] font-bold uppercase tracking-wider px-1">
                          <span>Set</span><span>Reps</span><span>Weight ({weightLabel})</span><span></span>
                        </div>
                        {ex.sets.map((set, setIdx) => (
                          <div key={setIdx} className="grid grid-cols-[1fr_2fr_2fr_auto] gap-2 items-center">
                            <span className="text-xs text-[#A1A1AA] text-center">{setIdx + 1}</span>
                            <input
                              type="number"
                              value={set.reps}
                              onChange={(e) => updateSet(exIdx, setIdx, "reps", parseInt(e.target.value) || 0)}
                              className="bg-[#161616] border border-[#27272A] text-white text-sm px-2 py-1.5 focus:border-[#E05A1D] outline-none"
                              data-testid={`set-reps-${exIdx}-${setIdx}`}
                            />
                            <input
                              type="number"
                              value={weightUnit === "lb" ? (set.weight ? Number(fromKg(set.weight).toFixed(1)) : set.weight) : set.weight}
                              onChange={(e) => {
                                const v = parseFloat(e.target.value) || 0;
                                updateSet(exIdx, setIdx, "weight", weightUnit === "lb" ? Number(toKg(v).toFixed(2)) : v);
                              }}
                              className="bg-[#161616] border border-[#27272A] text-white text-sm px-2 py-1.5 focus:border-[#E05A1D] outline-none"
                              data-testid={`set-weight-${exIdx}-${setIdx}`}
                            />
                            <button
                              onClick={() => removeSet(exIdx, setIdx)}
                              className="text-[#71717A] hover:text-red-500 transition-colors p-1"
                              data-testid={`remove-set-${exIdx}-${setIdx}`}
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => addSet(exIdx)}
                          className="text-xs text-[#A1A1AA] hover:text-[#E05A1D] font-semibold mt-1 transition-colors"
                          data-testid={`add-set-${exIdx}`}
                        >
                          + Add Set
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {exercises.length === 0 && (
                  <p className="text-sm text-[#71717A] text-center py-6 border border-dashed border-[#27272A]">
                    Click "Add Exercise" to get started
                  </p>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm h-20 resize-none focus:border-[#E05A1D] focus:ring-1 focus:ring-[#E05A1D] outline-none transition-all"
                  placeholder="How did it go?"
                  data-testid="workout-notes-input"
                />
              </div>

              {/* Save */}
              <button
                onClick={saveWorkout}
                disabled={saving}
                className="w-full bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide py-3 flex items-center justify-center gap-2 transition-colors disabled:opacity-50 active:scale-[0.98]"
                data-testid="save-workout-btn"
              >
                <FloppyDisk size={18} weight="bold" />
                {saving ? "Saving..." : "Save Workout"}
              </button>
            </div>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Workouts List */}
      <div className="space-y-3" data-testid="workouts-list">
        {workouts.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-[#27272A]">
            <Barbell size={48} className="text-[#27272A] mx-auto mb-3" />
            <p className="text-[#71717A] text-sm">No workouts yet. Start logging!</p>
          </div>
        ) : (
          workouts.map((w) => (
            <div key={w.id} className="exercise-row flex items-start justify-between" data-testid={`workout-card-${w.id}`}>
              <button
                className="flex-1 text-left cursor-pointer"
                onClick={() => onOpenWorkout?.(w)}
                data-testid={`open-workout-${w.id}`}
              >
                <div className="flex items-center gap-3 mb-1">
                  <h4 className="font-semibold text-white hover:text-[#E05A1D] transition-colors">{w.name}</h4>
                  <span className="text-xs text-[#71717A]">{w.date}</span>
                  {w.duration_minutes > 0 && (
                    <span className="text-xs text-[#A1A1AA] bg-[#1C1C1C] px-2 py-0.5">{w.duration_minutes} min</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {w.exercises?.map((ex, i) => (
                    <span key={i} className="text-xs text-[#A1A1AA] bg-[#0F0F0F] border border-[#27272A] px-2 py-1">
                      {ex.name} ({ex.sets?.length || 0} sets)
                    </span>
                  ))}
                </div>
              </button>
              <button
                onClick={() => deleteWorkout(w.id)}
                className="text-[#71717A] hover:text-red-500 transition-colors p-2"
                data-testid={`delete-workout-${w.id}`}
              >
                <Trash size={16} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
